/**
 * PracticeActivityRouter — the 5-archetype dispatcher (fix #252 /
 * #PL-WEB-RUBRICS).
 *
 * Takes the practice item's `scoring_rubric` + `activity_payload`,
 * validates the rubric, and picks one of the 5 archetype
 * renderers. Falls back to the `NotSetupScreen` if the rubric is
 * missing, malformed, or for an archetype that hasn't shipped
 * yet (AC #5).
 *
 * Picture_naming is special: the renderer reads trial shape
 * from `activity_payload.trials` (the format #251 already
 * shipped), not from the rubric. The router passes through a
 * `trial` prop alongside `activity_payload` because that was
 * the #251 contract.
 */
import type { ReactElement } from 'react';
import type { ActivityPayloadAny, PracticeActivityItemLike } from './shared';
import { isPracticeRubric, type PracticeRubric } from '../../../lib/practice/rubrics';
import type { TrialOutcome } from './shared';
import ConversationTurnsPlayer from './ConversationTurns';
import PictureNamingPlayer from './PictureNaming';
import {
  buildPictureNamingTrials,
  type PictureNamingTrial,
} from './pictureNamingTrials';
import NotSetupScreen from './NotSetupScreen';
import ReceptiveDirectionsPlayer from './ReceptiveDirections';
import SequencingPlayer from './Sequencing';
import SentenceCompletionPlayer from './SentenceCompletion';

interface RouterProps {
  item: PracticeActivityItemLike;
  trialIndex: number;
  totalTrials: number;
  onTrialComplete: (outcome: TrialOutcome) => void;
  onBackToActivities: () => void;
}

export default function PracticeActivityRouter(
  props: RouterProps,
): ReactElement {
  const rubric = props.item.scoring_rubric;

  if (isPracticeRubric(rubric)) {
    return renderByArchetype(props, rubric);
  }

  // Legacy payload-only picture_naming items (#251 shipped with
  // `activity_payload.trials` and no `scoring_rubric`). Do NOT
  // hard-gate these into the "not set up" fallback.
  const payload = props.item.activity_payload;
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { trials?: unknown }).trials) &&
    ((payload as { trials: unknown[] }).trials).length > 0
  ) {
    return renderPictureNaming(props, payload);
  }

  const reason =
    rubric === null
      ? 'No scoring rubric attached to this activity.'
      : 'This activity rubric is in an unexpected shape.';
  return (
    <NotSetupScreen
      reason={reason}
      onBackToActivities={props.onBackToActivities}
    />
  );
}

function renderByArchetype(
  props: RouterProps,
  rubric: PracticeRubric,
): ReactElement {
  switch (rubric.archetype) {
    case 'picture_naming':
      return renderPictureNaming(props, props.item.activity_payload);
    case 'receptive_directions':
      return (
        <ReceptiveDirectionsPlayer
          rubric={rubric}
          trialIndex={props.trialIndex}
          totalTrials={props.totalTrials}
          onTrialComplete={props.onTrialComplete}
        />
      );
    case 'sentence_completion':
      return (
        <SentenceCompletionPlayer
          rubric={rubric}
          trialIndex={props.trialIndex}
          totalTrials={props.totalTrials}
          onTrialComplete={props.onTrialComplete}
        />
      );
    case 'conversation_turns':
      return (
        <ConversationTurnsPlayer
          rubric={rubric}
          trialIndex={props.trialIndex}
          totalTrials={props.totalTrials}
          onTrialComplete={props.onTrialComplete}
        />
      );
    case 'sequencing':
      return (
        <SequencingPlayer
          rubric={rubric}
          trialIndex={props.trialIndex}
          totalTrials={props.totalTrials}
          onTrialComplete={props.onTrialComplete}
        />
      );
  }
}

function renderPictureNaming(
  props: RouterProps,
  payload: ActivityPayloadAny,
): ReactElement {
  // Clamp the playable count to the number of clinically-authored
  // valid trials in the payload (fix #292). Previously the
  // builder padded short payloads with fabricated "cat/dog/pig/cow"
  // placeholders, which the renderer then scored and posted to
  // api-record-practice-attempts as genuine attempts — polluting
  // per-item accuracy analytics for trials the clinician never
  // authored.
  const trials = buildPictureNamingTrials(payload, props.totalTrials);
  const playableTotal = Math.min(props.totalTrials, trials.length);
  if (playableTotal === 0) {
    return (
      <NotSetupScreen
        reason="This activity has no practice trials to play."
        onBackToActivities={props.onBackToActivities}
      />
    );
  }
  if (props.trialIndex >= trials.length) {
    // Past the last valid trial — the parent loop advanced beyond
    // what the payload actually contains. Treat as end-of-activity
    // instead of fabricating a placeholder.
    return (
      <NotSetupScreen
        reason="No more practice trials available."
        onBackToActivities={props.onBackToActivities}
      />
    );
  }
  // Narrowed: `trialIndex < trials.length` guarantees a defined element
  // under noUncheckedIndexedAccess; the previous code used a fabricated
  // cat/dog/pig/cow fallback here (the bug #292 fixes).
  const trial: PictureNamingTrial = trials[props.trialIndex]!;
  const minimalRubric = { archetype: 'picture_naming' as const };
  return (
    <PictureNamingPlayer
      rubric={minimalRubric}
      trial={trial}
      trialIndex={props.trialIndex}
      totalTrials={playableTotal}
      onTrialComplete={props.onTrialComplete}
    />
  );
}
